import axiosInstance from "./axios";
import { LoginCredentialsType, UserType } from "@/types/AuthTypes";
import { ResponseType } from "@/types/CommonTypes";

export interface LoginResponse {
  token?: string;
  success: boolean;
  message: string;
  requiresMfa?: boolean;
  portal_login_id?: number;
}

export interface VerifyMfaRequest {
  code: string;
  portal_login_id: number;
}

export interface VerifyMfaResponse {
  token: string;
  success: boolean;
  message: string;
}

export interface ValidateLTITokenResponse {
  token: string;
  user: UserType;
}

/** `POST /auth/exchange-skip-login-code` — same envelope as LTI; `data` has no `user` (load via getMe). */
export interface ExchangeSkipLoginCodeData {
  token: string;
  landing_page?: string;
}

export interface SelfRegisterRequest {
  name: string;
  email: string;
  password: string;
  organization: string;
  privacy_policy_id: number;
}

export interface SelfRegisterResponse {
  success: boolean;
  message: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  token?: string;
}

export const authApi = {
  login: async (credentials: LoginCredentialsType): Promise<LoginResponse> => {
    try {
      const response = await axiosInstance.post("/auth/login", credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await axiosInstance.post("/auth/logout");
    } catch (error) {
      throw error;
    }
  },
  /** Stop the current impersonation session (revokes it server-side + audits). */
  stopImpersonation: async (): Promise<void> => {
    try {
      await axiosInstance.post("/auth/stop-impersonation");
    } catch (error) {
      throw error;
    }
  },

  getMe: async (): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.get("/auth/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateMe: async (
    user: Partial<UserType>
  ): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.put("/auth/me", user);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updatePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.put("/auth/update-password", {
        currentPassword,
        newPassword,
      });
      if (response.data.success !== true) {
        throw new Error(response.data.message);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getPasswordRules: async (): Promise<ResponseType<string>> => {
    try {
      const response = await axiosInstance.get("/auth/password-rules");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post("/auth/forgot-password", {
        email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  validateLTIToken: async (
    ltiToken: string
  ): Promise<ResponseType<ValidateLTITokenResponse>> => {
    try {
      const response = await axiosInstance.post("/auth/validate-lti-token", {
        ltiToken,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  exchangeSkipLoginCode: async (
    code: string,
    requestConfig?: { signal?: AbortSignal }
  ): Promise<ResponseType<ExchangeSkipLoginCodeData>> => {
    try {
      const response = await axiosInstance.post<ResponseType<ExchangeSkipLoginCodeData>>(
        "/auth/exchange-skip-login-code",
        { code },
        requestConfig?.signal ? { signal: requestConfig.signal } : undefined
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  refreshToken: async (): Promise<LoginResponse> => {
    try {
      const response = await axiosInstance.post("/auth/refresh-token");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post("/auth/reset-password", {
        token,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  selfRegister: async (
    data: SelfRegisterRequest
  ): Promise<SelfRegisterResponse> => {
    try {
      const response = await axiosInstance.post("/auth/self-register", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyEmail: async (token: string): Promise<VerifyEmailResponse> => {
    try {
      const response = await axiosInstance.get("/auth/verify-email", {
        params: { token },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyMfa: async (request: VerifyMfaRequest): Promise<VerifyMfaResponse> => {
    try {
      const response = await axiosInstance.post("/auth/verify-mfa", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
